'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { LineMenu } from '@qr-front/icons/line-menu'
import { LineTrashAlt } from '@qr-front/icons/line-trash-alt'
import {
	SortDialog,
	SortDialogListItem,
} from 'features/generator/components/sortDialog'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'

import { useSyncFieldWithAtom } from '@/hooks'
import { getStore } from '@/store'

import { InputButton } from '../../InputButton'
import { getVideoFormSchema, TVideo, TVideoFields } from '../VideoFormModel'
import { VideoDescriptionField } from './VideoDescriptionField'
import { VideoPlayer } from './VideoPlayer'
import { VideoTitleField } from './VideoTitleField'

export const videoListAtom = atomWithReset<TVideo[]>([])
videoListAtom.debugLabel = 'videoList'
const showVideoSortDialogAtom = atomWithReset<boolean>(false)

export const VideoDetailsFields = forwardRef(
	({ onVideoRemove }: { onVideoRemove: (video: TVideo) => void }, ref) => {
		const t = useTranslations()
		const form = useForm<TVideoFields>({
			defaultValues: {
				videos: getStore().get(videoListAtom),
			},
			mode: 'onChange',
			reValidateMode: 'onChange',
			resolver: yupResolver(
				getVideoFormSchema(t),
			) as unknown as Resolver<TVideoFields>,
			shouldUnregister: false,
		})

		const { setValue, trigger, handleSubmit } = form

		const [videoList, setVideoList] = useAtom(videoListAtom)
		const setShowSortDialog = useSetAtom(showVideoSortDialogAtom)

		useSyncFieldWithAtom({
			atom: videoListAtom,
			setFieldValue: (value) => setValue('videos', value),
		})

		const handleClickRemove = (index: number) => {
			const removedVideo = videoList[index]
			setVideoList((prev) => prev.filter((_, i) => i !== index))
			onVideoRemove(removedVideo as TVideo)
			trigger('videos')
		}

		useImperativeHandle(
			ref,
			() => ({
				validate: () =>
					validateContentForm((onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					),
			}),
			[handleSubmit],
		)

		if (!videoList.length) {
			return null
		}

		return (
			<FormProvider {...form}>
				<form className="flex flex-col gap-4">
					<ul className="flex flex-col gap-2">
						{videoList.map((video, index) => (
							<li
								key={index}
								className="grid grid-cols-[120px_1fr] gap-4 rounded-10 bg-light-grey-70 p-6"
							>
								{/* Video + Buttons Row (Only on tablet and below) */}
								<div className="col-span-2 flex items-center gap-2 tablet:col-span-1 tablet:hidden">
									<div className="max-h-[80px] max-w-[120px]">
										<VideoPlayer item={video} size="small" />
									</div>
									<div className="ml-auto flex gap-2">
										<InputButton
											icon={<LineTrashAlt className="size-5" />}
											onClick={() => handleClickRemove(index)}
										/>
										{videoList.length > 1 && (
											<InputButton
												icon={<LineMenu className="size-5" />}
												onClick={() => setShowSortDialog(true)}
											/>
										)}
									</div>
								</div>

								{/* Desktop layout (video only in col 1) */}
								<div className="hidden max-h-[80px] tablet:block">
									<VideoPlayer item={video} size="small" />
								</div>

								{/* Title + Description + Buttons (Desktop) */}
								<div className="col-span-2 flex w-full flex-col gap-4 tablet:order-2 tablet:col-span-1 tablet:flex-row">
									<VideoTitleField index={index} videoList={videoList} />
									<VideoDescriptionField index={index} videoList={videoList} />
									<div className="hidden gap-2 tablet:flex">
										<InputButton
											className="mt-8"
											icon={<LineTrashAlt className="size-5" />}
											onClick={() => handleClickRemove(index)}
										/>
										{videoList.length > 1 && (
											<InputButton
												className="mt-8"
												icon={<LineMenu className="size-5" />}
												onClick={() => setShowSortDialog(true)}
											/>
										)}
									</div>
								</div>
							</li>
						))}
					</ul>

					<SortDialog
						title={t(
							'generator__content_form_section__videos__sort_dialog_title',
						)}
						list={videoList}
						onSave={setVideoList}
						isOpenAtom={showVideoSortDialogAtom}
						renderItem={(item, index, onSortChange) => (
							<SortDialogListItem
								itemsLength={videoList.length}
								key={item.video.name}
								name={item.video.name}
								index={index}
								onSortChange={onSortChange}
							/>
						)}
					/>
				</form>
			</FormProvider>
		)
	},
)

VideoDetailsFields.displayName = 'VideoDetailsFields'
